import { useEffect, useState } from 'react';
import axios from 'axios';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useConsole from '../Console/useConsole';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import utils from '../../utils/utils';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';

const ME_BASE_URL = ApiEndpoints.MODULE_BASE_URL.ME;
const MD_BASE_URL = ApiEndpoints.MODULE_BASE_URL.MD; // Model Deployment base url
const totalPageCount = 'x-total-count';

/**
 * @function useModelEstimation
 * @description hook for Model Estimation which contains API calls and common state data
 * @param {function} fetchModuleTasks - API call for fetching module tasks
 * @param {array} datasetList - list of datasets
 * @param {function} fetchDatasetList - API call for fetching datasets
 * @param {function} fetchDatasetRowsWithoutPagination - API call for fetching dataset rows without pagination
 * @param {array} datasetRowsWithoutPagination - dataset rows without pagination
 * @param {function} setDatasetRowsWithoutPagination - function to set dataset rows without pagination
 * @param {array} datasetRowsWithPagination - dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - function to set dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {function} fetchDatasetRowsWithPagination - API call for fetching dataset rows with pagination
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - boolean to check if API is in progress
 * @param {function} updateDatasetCells - API call for updating dataset cells
 * @param {object} project - project details
 * @returns {object} - methods and state data to be used in Model Estimation
 * @example const {fetchModuleTasks,datasetList,fetchDatasetList}=useModelEstimation()
 */

export default function useModelEstimation() {
  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  const {
    fetchModuleTasks,
    datasetList,
    fetchDatasetList,

    fetchDatasetRowsWithoutPagination,
    datasetRowsWithoutPagination,
    setDatasetRowsWithoutPagination,

    datasetRowsWithPagination,
    setDatasetRowsWithPagination,

    totalDatasetRows,
    fetchDatasetRowsWithPagination,
    datasetRowsPageCount,
    isLoading,
    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();

  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData: fetchConsoleTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();

  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Operation
  const [activeOperation, setActiveOperation] = useState({});
  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  // For Column Details of Active Dataset
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // Preliminary Steps Tasks
  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  // Target variables/columns for dropdown
  const [targetVariables, setTargetVariables] = useState([]);

  // For paramters
  const [taskDetails, updateTaskDetails] = useState({});

  // For Missing Column Dataset Details
  const [missingDatasetColumn, setMissingDatasetColumn] = useState([]);

  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  // Saved Models
  const [savedModels, setsavedModels] = useState(null);

  // deplou model
  const [deployedTableId, setdeployedTableId] = useState(null);

  // for redirecting the user to metric selection if the
  // project is changed while the results is being displayed
  // this is to prevent breakage in UI
  useEffect(() => {
    updateTaskDetails({});
    setActiveDataset('');
  }, [project.activeProject]);

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(`${ME_BASE_URL + ApiEndpoints.ME.GET_ME_TASKS}/${taskID}/${type}/`)
      .then((res) => {
        return res;
      });

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset's column details (type API)
   * @param {object} datasetName - payload for API call, contains project_id and table_name
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnDetails(datasetName);
   */

  const fetchDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME +
          ApiEndpoints.ME.DATASET_COLUMN_DETAILS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.columns);
          return res;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function fetchMissingDatasetColumnDetails
   * @description API call for fetching missing dataset's column details with missing values (type API)
   * @param {object} datasetName - payload for API call, contains project_id and dataset_id
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnDetails(datasetName);
   */

  const fetchMissingDatasetColumnDetails = (datasetName) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.EDA +
          ApiEndpoints.EDA.EDA_MVI_GET_COLUMNS
        }`,
        datasetName
      )
      .then(
        (res) => {
          setMissingDatasetColumn(res?.data?.Columnlist);
          return res;
        },
        (error) => {
          throw error;
        }
      );
  /**
   * @function fetchMEModuleTasks
   * @description API call for fetching Model Estimation(leaderboard) tasks.
   * @param {number} projectId - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {void}
   * @example fetchBTModuleTasks(projectId,hideLoaderOnRequest);
   */

  const fetchMEModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.subModules.ME.key,
        'GET_ME_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };
  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.subModules.ME.key,
        'GET_ME_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  /**
   * @function fetchMEModuleTasksConsole
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {void}
   * @example fetchMEModuleTasksConsole(projectId,hideLoaderOnRequest);
   */
  const fetchMEModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.subModules.ME.key,
        'GET_ME_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };

  /**
   * @function fetchTargetVariableList
   * @description API call to get all the target variables/columns(binary) in the dataset.
   * @param {object} datasetName - contains name of the dataset and project id.
   * @returns {Promise} AxiosPromise
   * @example fetchTargetVariableList(datasetName);
   */

  const fetchTargetVariableList = (datasetName) => {
    setTargetVariables([]);
    return axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.GET_TARGET_LIST}`,
        datasetName
      )
      .then(
        (res) => {
          if (res?.data?.valid_targets.length) {
            setTargetVariables(res.data.valid_targets);
          }
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {string} taskID - task id for the task selected from job queue
   * @param {object} additionTaskDetails - task details for the task selected from job queue
   * @returns {Promise} AxiosPromise
   * @example fetchAsyncResult(taskID, additionTaskDetails);
   */

  const fetchAsyncResult = (taskID, additionTaskDetails) =>
    axios
      .get(`${`${ME_BASE_URL + ApiEndpoints.ME.GET_ME_TASKS}/${taskID}`}`)
      .then(
        (res) => {
          if (res?.data?.task_status === 'SUCCESS') {
            Object.values(
              MODULE_SUB_MODULE_OPERATIONS_TREE.ME.subModules.ME.operations
            ).filter((data) => {
              if (
                data?.key === additionTaskDetails?.operation &&
                res?.data?.parameters?.dataset_id
              ) {
                datasetList.forEach((dataset) => {
                  if (
                    dataset?.id === Number(res?.data?.parameters?.dataset_id)
                  ) {
                    setActiveDataset(dataset);
                  }
                });

                updateTaskDetails(res.data);
                setActiveOperation(data);
              }
              return null;
            });
          } else {
            updateTaskDetails({});
          }
        },
        (error) => {
          throw error;
        }
      );
  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS, 'fields'].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function terminateTask
   * @description API call for terminating task in the job queue
   * @param {string} taskID - task id for the task selected from job queue
   * @returns {Promise} AxiosPromise
   * @example terminateTask(taskID);
   */
  const terminateTask = (taskID) =>
    axios
      .delete(`${`${ME_BASE_URL + ApiEndpoints.ME.GET_ME_TASKS}/${taskID}`}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function fetchSavedModelList
   * @description API call for fetching list of saved models
   * @param {number} projectId - object containing project id
   * @param {string} field - fields for fetching saved model list
   * @returns {Promise} AxiosPromise
   * @example fetchSavedModelList(projectId);
   */
  const fetchSavedModelList = (projectId, field) =>
    axios
      .get(`${ME_BASE_URL + ApiEndpoints.ME.ME_GET_SAVED_MODEL_LIST}`, {
        params: {
          project_id: projectId,
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          setsavedModels(res.data);
          return res.data;
        },
        (error) => {
          throw error;
        }
      );

  /**
   * @function deploySavedModel
   * @description API call for deploying the saved model.
   * @param {object} body - contains data selected by user for the operation.
   * @returns {Promise} AxiosPromise
   * @example deploySavedModel(body);
   */

  const deploySavedModel = (body) =>
    axios.post(`${ME_BASE_URL + ApiEndpoints.ME.ME_DEPLOY_MODEL}`, body).then(
      (res) => {
        setdeployedTableId(res.data.response);
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function deploySavedModelWithConfig
   * @description API call for deploying the saved model with configs.
   * @param {object} body - contains data selected by user for the operation.
   * @returns {Promise} AxiosPromise
   * @example deploySavedModelWithConfig(body);
   */

  const deploySavedModelWithConfig = (body) =>
    axios
      .post(
        `${MD_BASE_URL + ApiEndpoints.ME.ME_DEPLOY_MODEL_WITH_CONFIGS}`,
        body
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function markTaskReadUnreadModelEstimation
   * @description API call for mark task status read/unread for Leaderboard tasks.
   * @param {object} taskParams -task parameters for marking task status read/unread for Leaderboard
   * @returns {Promise} AxiosPromise
   * @example markTaskReadUnreadModelEstimation(taskParams);
   */

  const markTaskReadUnreadModelEstimation = (taskParams) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.ME_JOB_QUEUE_MARK_READ_UNREAD, {
        params: taskParams,
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function onModelInitiationScoreCardSubmit
   * @description API call to get feature importance operation result
   * @param {object} body - object containing necessary form data for API call
   * @returns {Promise} AxiosPromise
   * @example onModelInitiationScoreCardSubmit(body);
   */
  const onModelInitiationScoreCardSubmit = (body) =>
    axios
      .post(
        `${ME_BASE_URL + ApiEndpoints.ME.ME_SC_MODEL_INITIATION_SUBMIT}`,
        body
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function onModelInitiationTimeSeriesSubmit
   * @description API call to get feature importance operation result
   * @param {object} body - object containing necessary form data for API call
   * @returns {Promise} AxiosPromise
   * @example onModelInitiationTimeSeriesSubmit(body);
   */
  const onModelInitiationTimeSeriesSubmit = (body) =>
    axios
      .post(
        `${ME_BASE_URL + ApiEndpoints.ME.ME_TS_MODEL_INITIATION_SUBMIT}`,
        body
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  /**
   * @function  renameModelDisplayName
   * @description API call for renaming dataset display name.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  renameModelDisplayName(payload)
   */
  const renameModelDisplayName = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME +
          ApiEndpoints.ME.RENAME_MODEL_DISPLAY_NAME
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function  fetchBigDataStatus
   * @description API call for fetching status  of big dataset.
   * @param {object} tableName - object containing necessary data for
   * API call
   * @returns {Promise} AxiosPromise
   * @example  fetchBigDataStatus(tableName)
   */

  const fetchBigDataStatus = (tableName) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.ME_GET_BIG_DATA_STATUS, {
        params: { dataset_id: tableName.id },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  /**
   * @function  meRerunOperation
   * @description API call for rerunning the operation.
   * @param {object} body - payload data
   * @returns {Promise} AxiosPromise
   * @example  meRerunOperation(body)
   */

  const meRerunOperation = (body) => {
    return axios
      .post(`${ME_BASE_URL + ApiEndpoints.ME.ME_RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    datasetColumnDetails,
    fetchDatasetColumnDetails,
    fetchMissingDatasetColumnDetails,
    missingDatasetColumn,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchMEModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    renameModelDisplayName,

    taskDetails,
    resetTaskDetails,
    fetchAsyncResult,

    meRerunOperation,

    // saved models
    savedModels,
    fetchSavedModelList,

    // deploy model
    deployedTableId,
    deploySavedModel,
    deploySavedModelWithConfig,

    targetVariables,
    isLoading,
    fetchTargetVariableList,

    datasetList,
    fetchDatasetList,

    terminateTask,

    // model-Initiation
    onModelInitiationScoreCardSubmit,
    onModelInitiationTimeSeriesSubmit,

    fetchDatasetRowsWithoutPagination,
    datasetRowsWithoutPagination,
    setDatasetRowsWithoutPagination,

    datasetRowsWithPagination,
    setDatasetRowsWithPagination,

    totalDatasetRows,
    fetchDatasetRowsWithPagination,
    datasetRowsPageCount,

    updateDatasetCells,
    gridOperationRerun,
    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    markTaskReadUnreadModelEstimation,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    project,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    fetchBigDataStatus,
    markDatasetAsReadyToUse,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchMEModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    setConsoleAsyncTaskResults,
    fetchConsoleTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchConsoleFileList,
    fetchAsyncResultSplit,
  };
}
